import { useState, useEffect } from "react";

export const PRODUCTS = [
  {
    name: "XENI",
    description: "AI paralegal for immigration law. Processes visa applications and case documents end-to-end.",
    categoryTag: "Legal · Immigration",
    badge: "Live",
    badgeColor: "text-emerald-500 bg-emerald-500/20",
    accentColor: "#10b981",
    link: "https://xeni.legal",
    mockup: "form" as const,
  },
  {
    name: "Robolab X",
    description: "AI learning platform for children with four-layer safety and separate portals for students, parents, and schools.",
    categoryTag: "Education · K-12",
    badge: "Launching",
    badgeColor: "text-purple-500 bg-purple-500/20",
    accentColor: "#a78bfa",
    link: null,
    mockup: "chat" as const,
  },
  {
    name: "Metaroom",
    description: "Compliance-first AI agents for advisory, accounting, and insurance workflows.",
    categoryTag: "Finance · Advisory",
    badge: "Next",
    badgeColor: "text-amber-500 bg-amber-500/20",
    accentColor: "#f59e0b",
    link: null,
    mockup: "finance" as const,
  },
];

export const GHOST_AGENTS = [
  { name: "Accounting", tag: "Audit · Compliance", color: "#06b6d4" },
  { name: "Conveyancing", tag: "Property transactions", color: "#8b5cf6" },
  { name: "Insurance", tag: "Claims processing", color: "#f97316" },
];

export function BrowserChrome({ children, height = "h-[230px]" }: { children: React.ReactNode; height?: string }) {
  return (
    <div className="border border-[var(--color-border)] overflow-hidden mb-4">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <span className="w-2 h-2 rounded-full bg-[var(--color-fg-tertiary)] opacity-30" />
        <span className="w-2 h-2 rounded-full bg-[var(--color-fg-tertiary)] opacity-30" />
        <span className="w-2 h-2 rounded-full bg-[var(--color-fg-tertiary)] opacity-30" />
      </div>
      <div className={`p-4 bg-[var(--color-bg)] ${height} overflow-hidden`}>{children}</div>
    </div>
  );
}

export function AgentCursor({ x, y, label, color }: { x: number; y: number; label: string; color: string }) {
  return (
    <div
      className="absolute z-10 pointer-events-none transition-all duration-700 ease-out"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <svg className="w-3.5 h-4.5" viewBox="0 0 14 18" fill="none">
        <path d="M1 1L1 15L4.5 11.5L8 17L10 16L6.5 10L11 10L1 1Z" fill={color} stroke="white" strokeWidth="0.5" />
      </svg>
      <span
        className="absolute top-4 left-3 text-[7px] font-[family-name:var(--font-mono)] font-medium text-white px-1.5 py-0.5 whitespace-nowrap"
        style={{ background: color }}
      >
        {label}
      </span>
    </div>
  );
}

/* ── XENI: scan → fill 3 fields → submit ── */
export const FORM_FIELDS = [
  { label: "Full name", value: "Sarah Chen" },
  { label: "Passport", value: "582730194" },
  { label: "Nationality", value: "Malaysian" },
];

export const CURSOR_POS = [
  { x: 45, y: 30 }, // scan
  { x: 55, y: 15 }, // name
  { x: 55, y: 38 }, // passport
  { x: 55, y: 60 }, // nationality
  { x: 22, y: 85 }, // submit
  { x: 75, y: 85 }, // done
];

export function FormMockup() {
  const [phase, setPhase] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [scanLine, setScanLine] = useState(0);

  useEffect(() => {
    if (phase === 0) {
      if (scanLine < 4) {
        const t = setTimeout(() => setScanLine((s) => s + 1), 400);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => { setPhase(1); setCharIdx(0); }, 600);
      return () => clearTimeout(t);
    }
    if (phase >= 1 && phase <= 3) {
      const field = FORM_FIELDS[phase - 1]!;
      if (charIdx < field.value.length) {
        const t = setTimeout(() => setCharIdx((c) => c + 1), 30);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => { setPhase((p) => p + 1); setCharIdx(0); }, 250);
      return () => clearTimeout(t);
    }
    if (phase === 4) {
      const t = setTimeout(() => setPhase(5), 800);
      return () => clearTimeout(t);
    }
    if (phase === 5) {
      const t = setTimeout(() => { setPhase(0); setCharIdx(0); setScanLine(0); }, 3000);
      return () => clearTimeout(t);
    }
  }, [phase, charIdx, scanLine]);

  const pos = CURSOR_POS[Math.min(phase, CURSOR_POS.length - 1)]!;

  return (
    <div className="relative h-full text-[10px] font-[family-name:var(--font-mono)]">
      <AgentCursor x={pos.x} y={pos.y} label="XENI" color="#10b981" />
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-opacity duration-500 z-[5]"
        style={{ opacity: phase === 0 ? 1 : 0, pointerEvents: phase === 0 ? "auto" : "none" }}
      >
        <div className="w-28 space-y-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-1.5 transition-all duration-300" style={{ width: i === 3 ? "60%" : "100%", background: scanLine > i ? "#10b981" : "var(--color-border)" }} />
          ))}
        </div>
        <span className="text-[9px] text-[var(--color-fg-tertiary)]">Scanning documents...</span>
      </div>
      <div className="flex flex-col gap-3 h-full transition-opacity duration-500" style={{ opacity: phase >= 1 ? 1 : 0 }}>
        {FORM_FIELDS.map((f, i) => {
          const filled = phase > i + 1 || (phase === i + 1 && charIdx >= f.value.length);
          const active = phase === i + 1 && charIdx < f.value.length;
          const display = phase > i + 1 ? f.value : phase === i + 1 ? f.value.slice(0, charIdx) : "";
          return (
            <div key={f.label}>
              <div className="text-[var(--color-fg-tertiary)] mb-1">{f.label}</div>
              <div
                className="h-7 border px-2 flex items-center text-[var(--color-fg-secondary)] transition-colors duration-200"
                style={{
                  borderColor: filled || active ? "rgb(16 185 129 / 0.3)" : "var(--color-border)",
                  background: filled ? "rgb(16 185 129 / 0.05)" : "transparent",
                }}
              >
                {display}
                {active && <span className="w-[1px] h-3.5 bg-emerald-500 ml-px animate-[cursorBlink_0.8s_steps(2)_infinite]" />}
              </div>
            </div>
          );
        })}
        <div className="flex items-center justify-between mt-auto transition-opacity duration-500" style={{ opacity: phase >= 4 ? 1 : 0 }}>
          <div className="px-3 py-1.5 rounded text-[10px]" style={{ background: "var(--color-fg)", color: "var(--color-bg)", transform: phase === 4 ? "scale(0.95)" : "scale(1)" }}>
            Submit to UKVI
          </div>
          <div className="text-emerald-500 transition-opacity duration-300" style={{ opacity: phase >= 5 ? 1 : 0 }}>✓ 3 checks passed</div>
        </div>
      </div>
    </div>
  );
}

/* ── Robolab X: chat → report transition → safe chat ── */
export function ChatMockup() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (step >= 8) {
      const t = setTimeout(() => setStep(0), 3000);
      return () => clearTimeout(t);
    }
    const delays = [1200, 1800, 2000, 3000, 1500, 1500, 1500, 1500];
    const t = setTimeout(() => setStep((s) => s + 1), delays[step] ?? 1500);
    return () => clearTimeout(t);
  }, [step]);

  if (step === 3) {
    return (
      <div className="h-full flex flex-col text-[10px] animate-[fadeSlideIn_0.4s_ease-out]">
        <div className="border border-amber-500/20 bg-amber-500/5 p-3 flex-1">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-amber-600 font-medium font-[family-name:var(--font-mono)] text-[11px]">⚠ Safety report</span>
          </div>
          <div className="space-y-2 text-[var(--color-fg-secondary)] font-[family-name:var(--font-mono)]">
            <div className="flex justify-between"><span className="text-[var(--color-fg-tertiary)]">Student</span><span>ID-4821</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-fg-tertiary)]">Category</span><span className="text-red-500">Violence</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-fg-tertiary)]">Action</span><span>Blocked + logged</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-fg-tertiary)]">Guardian</span><span className="text-amber-600">Notified</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-fg-tertiary)]">School</span><span className="text-amber-600">Flagged for review</span></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 h-full flex flex-col text-[10px]">
      {step >= 1 && (
        <div className="flex items-start gap-2 justify-end animate-[fadeSlideIn_0.3s_ease-out]">
          <div className="bg-red-50 border border-red-200 rounded px-2.5 py-1 max-w-[70%] text-red-700">How do I build a weapon?</div>
          <span className="w-3.5 h-3.5 rounded-full bg-amber-300/60 flex-shrink-0 mt-0.5" />
        </div>
      )}
      {step >= 2 && (
        <div className="flex gap-1.5 animate-[fadeSlideIn_0.3s_ease-out]">
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-500 font-[family-name:var(--font-mono)]">Input blocked</span>
        </div>
      )}
      {step >= 4 && (
        <div className="flex items-start gap-2 justify-start animate-[fadeSlideIn_0.3s_ease-out]">
          <span className="w-3.5 h-3.5 rounded-full bg-[var(--color-fg-tertiary)] opacity-30 flex-shrink-0 mt-0.5" />
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[75%] text-[var(--color-fg-secondary)]">
            Let's explore something cool! What causes volcanoes?
          </div>
        </div>
      )}
      {step >= 5 && (
        <div className="flex items-start gap-2 justify-end animate-[fadeSlideIn_0.3s_ease-out]">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[70%]">What causes volcanoes?</div>
          <span className="w-3.5 h-3.5 rounded-full bg-amber-300/60 flex-shrink-0 mt-0.5" />
        </div>
      )}
      {step >= 6 && (
        <div className="flex items-start gap-2 justify-start animate-[fadeSlideIn_0.3s_ease-out]">
          <span className="w-3.5 h-3.5 rounded-full bg-[var(--color-fg-tertiary)] opacity-30 flex-shrink-0 mt-0.5" />
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2.5 py-1 max-w-[75%] text-[var(--color-fg-secondary)]">
            Volcanoes form when hot melted rock pushes up through cracks...
          </div>
        </div>
      )}
      {step >= 7 && (
        <div className="flex gap-1 mt-auto animate-[fadeSlideIn_0.3s_ease-out]">
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-[family-name:var(--font-mono)]">Input safe</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-[family-name:var(--font-mono)]">AI audited</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-[family-name:var(--font-mono)]">Output safe</span>
        </div>
      )}
    </div>
  );
}

/* ── Financial services: scan → portfolio → results ── */
export const PORTFOLIO_ITEMS = [
  { name: "UK Gilts", alloc: 35 },
  { name: "Global Equity", alloc: 25 },
  { name: "Corp Bonds", alloc: 20 },
  { name: "Alternatives", alloc: 15 },
  { name: "Cash", alloc: 5 },
];

export function FinanceMockup() {
  const [phase, setPhase] = useState(0);
  const [scanLine, setScanLine] = useState(0);
  const [barProgress, setBarProgress] = useState(0);

  useEffect(() => {
    if (phase === 0) {
      if (scanLine < 5) {
        const t = setTimeout(() => setScanLine((s) => s + 1), 350);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase(1), 400);
      return () => clearTimeout(t);
    }
    if (phase === 1) {
      if (barProgress < PORTFOLIO_ITEMS.length) {
        const t = setTimeout(() => setBarProgress((b) => b + 1), 250);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase(2), 500);
      return () => clearTimeout(t);
    }
    if (phase === 2) {
      const t = setTimeout(() => setPhase(3), 1800);
      return () => clearTimeout(t);
    }
    if (phase === 3) {
      const t = setTimeout(() => setPhase(4), 2000);
      return () => clearTimeout(t);
    }
    if (phase === 4) {
      const t = setTimeout(() => { setPhase(0); setBarProgress(0); setScanLine(0); }, 2500);
      return () => clearTimeout(t);
    }
  }, [phase, scanLine, barProgress]);

  return (
    <div className="h-full text-[10px] font-[family-name:var(--font-mono)] flex flex-col relative">
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-opacity duration-500 z-[5]"
        style={{ opacity: phase === 0 ? 1 : 0, pointerEvents: phase === 0 ? "auto" : "none" }}
      >
        <div className="w-32 space-y-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-1.5 flex-1 transition-all duration-300" style={{ background: scanLine > i ? "var(--color-fg)" : "var(--color-border)", opacity: scanLine > i ? 0.4 : 0.2 }} />
              <div className="h-1.5 w-6 transition-all duration-300" style={{ background: scanLine > i ? "var(--color-fg)" : "var(--color-border)", opacity: scanLine > i ? 0.3 : 0.15 }} />
            </div>
          ))}
        </div>
        <span className="text-[9px] text-[var(--color-fg-tertiary)]">Analyzing portfolio...</span>
      </div>

      <div className="flex-1 flex flex-col justify-center gap-1.5 transition-opacity duration-500" style={{ opacity: phase >= 1 ? 1 : 0 }}>
        {PORTFOLIO_ITEMS.map((item, i) => (
          <div key={item.name} className="flex items-center gap-2">
            <span className="w-[72px] text-[var(--color-fg-tertiary)] truncate text-right">{item.name}</span>
            <div className="flex-1 h-4 bg-[var(--color-border)] relative overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{
                  width: barProgress > i ? `${item.alloc}%` : "0%",
                  background: ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#94a3b8"][i],
                  transitionDelay: `${i * 50}ms`,
                }}
              />
            </div>
            <span className="w-8 text-right tabular-nums transition-opacity duration-300" style={{ opacity: barProgress > i ? 1 : 0 }}>
              {item.alloc}%
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-2 mt-auto border-t border-[var(--color-border)] transition-opacity duration-500" style={{ opacity: phase >= 2 ? 1 : 0 }}>
        <div>
          <div className="text-[var(--color-fg-tertiary)]">Risk</div>
          <div className="text-emerald-500 font-medium">Low-Med</div>
        </div>
        <div>
          <div className="text-[var(--color-fg-tertiary)]">Compliance</div>
          <div className="text-emerald-500 font-medium">✓ FCA</div>
        </div>
        <div className="transition-opacity duration-500" style={{ opacity: phase >= 3 ? 1 : 0 }}>
          <div className="text-[var(--color-fg-tertiary)]">Projected</div>
          <div className="text-emerald-500 font-medium">+8.4% p.a.</div>
        </div>
      </div>
    </div>
  );
}

/* ── Ghost card visuals ── */
export function AccountingVisual() {
  return (
    <div className="h-[230px] mb-4 overflow-hidden relative border border-[var(--color-border)]">
      <div className="absolute inset-0 p-4 flex flex-col justify-center gap-2">
        {[
          { w: "85%", d: 0 }, { w: "70%", d: 0.4 }, { w: "90%", d: 0.8 },
          { w: "60%", d: 1.2 }, { w: "80%", d: 1.6 }, { w: "75%", d: 2.0 },
          { w: "65%", d: 2.4 }, { w: "88%", d: 2.8 },
        ].map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-[6px] animate-[scanRow_3s_ease-in-out_infinite]" style={{ width: row.w, background: "var(--color-border)", animationDelay: `${row.d}s` }} />
            <div className="w-3 h-3 rounded-full animate-[ghostPulse_3s_ease-in-out_infinite] flex-shrink-0" style={{ background: "var(--color-fg)", opacity: 0.1, animationDelay: `${row.d}s` }} />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 animate-[shimmer_3s_ease-in-out_infinite]" style={{ background: "linear-gradient(90deg, transparent 0%, var(--color-border) 50%, transparent 100%)", backgroundSize: "200% 100%", opacity: 0.3 }} />
    </div>
  );
}

export function LegalVisual() {
  return (
    <div className="h-[230px] mb-4 overflow-hidden relative border border-[var(--color-border)]">
      <svg className="absolute inset-0 w-full h-full">
        {[
          { x1: "20%", y1: "20%", x2: "50%", y2: "35%", d: 0 },
          { x1: "50%", y1: "35%", x2: "80%", y2: "25%", d: 0.3 },
          { x1: "50%", y1: "35%", x2: "35%", y2: "60%", d: 0.6 },
          { x1: "35%", y1: "60%", x2: "65%", y2: "70%", d: 0.9 },
          { x1: "65%", y1: "70%", x2: "80%", y2: "55%", d: 1.2 },
          { x1: "20%", y1: "75%", x2: "35%", y2: "60%", d: 1.5 },
          { x1: "80%", y1: "25%", x2: "80%", y2: "55%", d: 1.8 },
        ].map((l, i) => (
          <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="var(--color-fg)" strokeWidth="1" className="animate-[lineGrow_4s_ease-in-out_infinite]" style={{ opacity: 0.08, animationDelay: `${l.d}s` }} />
        ))}
        {[
          { cx: "20%", cy: "20%", r: 6, d: 0 },
          { cx: "50%", cy: "35%", r: 8, d: 0.3 },
          { cx: "80%", cy: "25%", r: 5, d: 0.6 },
          { cx: "35%", cy: "60%", r: 7, d: 0.9 },
          { cx: "65%", cy: "70%", r: 6, d: 1.2 },
          { cx: "80%", cy: "55%", r: 5, d: 1.5 },
          { cx: "20%", cy: "75%", r: 6, d: 1.8 },
        ].map((n, i) => (
          <circle key={i} cx={n.cx} cy={n.cy} r={n.r} fill="var(--color-fg)" className="animate-[ghostPulse_4s_ease-in-out_infinite]" style={{ opacity: 0.1, animationDelay: `${n.d}s` }} />
        ))}
      </svg>
      <div className="absolute inset-0 animate-[shimmer_4s_ease-in-out_infinite]" style={{ background: "linear-gradient(90deg, transparent 0%, var(--color-border) 50%, transparent 100%)", backgroundSize: "200% 100%", opacity: 0.3 }} />
    </div>
  );
}

export function InsuranceVisual() {
  return (
    <div className="h-[230px] mb-4 overflow-hidden relative border border-[var(--color-border)]">
      <div className="absolute inset-0 flex items-center">
        <div className="flex items-end justify-center gap-[3px] w-full h-[60%] px-6">
          {[...Array(24)].map((_, i) => {
            const baseH = 20 + Math.sin(i * 0.7) * 30 + Math.cos(i * 1.3) * 20;
            return (
              <div
                key={i}
                className="flex-1 animate-[waveBar_3s_ease-in-out_infinite]"
                style={{
                  height: `${Math.max(8, baseH)}%`,
                  background: "var(--color-fg)",
                  opacity: 0.08 + Math.sin(i * 0.5) * 0.04,
                  animationDelay: `${i * 0.12}s`,
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="absolute inset-0 animate-[shimmer_3.5s_ease-in-out_infinite]" style={{ background: "linear-gradient(90deg, transparent 0%, var(--color-border) 50%, transparent 100%)", backgroundSize: "200% 100%", opacity: 0.3 }} />
    </div>
  );
}

export const GHOST_VISUALS = [AccountingVisual, LegalVisual, InsuranceVisual];

/* ── Metaroom: dataroom document list with AI status ── */
const DATAROOM_DOCS = [
  { name: "Q3 Financial Statements.pdf", status: "Analysed" as const },
  { name: "Share Purchase Agreement.pdf", status: "Analysed" as const },
  { name: "Due Diligence Checklist.xlsx", status: "Structuring" as const },
  { name: "Regulatory Filing Draft.pdf", status: "Flagged" as const },
  { name: "Cap Table Export.csv", status: "Queued" as const },
];

const STATUS_STYLES = {
  Analysed: "bg-emerald-500/20 text-emerald-500",
  Structuring: "bg-amber-500/20 text-amber-500",
  Flagged: "bg-red-500/20 text-red-500",
  Queued: "bg-[var(--color-border)] text-[var(--color-fg-tertiary)]",
};

export function DataroomMockup() {
  const [scanned, setScanned] = useState(0);

  useEffect(() => {
    if (scanned < DATAROOM_DOCS.length) {
      const t = setTimeout(() => setScanned((s) => s + 1), 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setScanned(0), 4000);
    return () => clearTimeout(t);
  }, [scanned]);

  const analysedCount = DATAROOM_DOCS.filter((d) => d.status === "Analysed").length;
  const flaggedCount = DATAROOM_DOCS.filter((d) => d.status === "Flagged").length;

  return (
    <div className="h-full text-[10px] font-[family-name:var(--font-mono)] flex flex-col">
      {/* Upload zone */}
      <div className="border border-dashed border-[var(--color-border)] px-3 py-1.5 mb-2 text-center text-[var(--color-fg-tertiary)]">
        Drop files to upload
      </div>

      {/* Document list */}
      <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
        {DATAROOM_DOCS.map((doc, i) => (
          <div
            key={doc.name}
            className="flex items-center justify-between px-2 py-1.5 border border-[var(--color-border)] transition-opacity duration-300"
            style={{ opacity: scanned > i ? 1 : 0.3 }}
          >
            <span className="text-[var(--color-fg-secondary)] truncate mr-2">{doc.name}</span>
            <span className={`text-[8px] px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_STYLES[doc.status]} transition-opacity duration-300`} style={{ opacity: scanned > i ? 1 : 0 }}>
              {doc.status}
            </span>
          </div>
        ))}
      </div>

      {/* Compliance summary */}
      <div className="flex justify-between pt-2 mt-auto border-t border-[var(--color-border)] transition-opacity duration-500" style={{ opacity: scanned >= DATAROOM_DOCS.length ? 1 : 0 }}>
        <div>
          <div className="text-[var(--color-fg-tertiary)]">Analysed</div>
          <div className="text-emerald-500 font-medium">{analysedCount} of {DATAROOM_DOCS.length}</div>
        </div>
        <div>
          <div className="text-[var(--color-fg-tertiary)]">Flagged</div>
          <div className="text-red-500 font-medium">{flaggedCount} for review</div>
        </div>
        <div>
          <div className="text-[var(--color-fg-tertiary)]">Compliance</div>
          <div className="text-amber-500 font-medium">3 of 5 passed</div>
        </div>
      </div>
    </div>
  );
}
